import React, {useState, useEffect, useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import {motion} from 'framer-motion';
import {Sword, Plus, Search, Filter, Users, TrendingUp, Shield, Clock} from 'lucide-react';
import {useAuth} from '../contexts/AuthContext';
import {charactersService} from '../services';
import PageContainer from '../components/PageContainer';
import EmptyState from '../components/EmptyState';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import {LoadingSpinner} from '../components/ui/Loading';
import CreateCharacterModal from '../components/CreateCharacterModal';
import EditCharacterModal from '../components/EditCharacterModal';
import {getCharacterStatsText, getClassTypeIcon, getClassTypeTranslation} from "../utils/characterTranslation";
import {formatRelativeTime} from '../utils/dateUtils';

const Characters = () => {
  const {user} = useAuth();
  const navigate = useNavigate();
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [error, setError] = useState('');

  // Filters and pagination
  const [filters, setFilters] = useState({
    search: '',
    class: '',
    minLevel: '',
    maxLevel: '',
    status: 'ACTIVE',
    userName: ''
  });
  const [searchInput, setSearchInput] = useState(''); // Input separado para busca
  const [userNameInput, setUserNameInput] = useState(''); // Input separado para nome do usuário
  const [minLevelInput, setMinLevelInput] = useState(''); // Input separado para nível mínimo
  const [maxLevelInput, setMaxLevelInput] = useState(''); // Input separado para nível máximo
  const [sortBy, setSortBy] = useState('level'); // Campo de ordenação
  const [sortOrder, setSortOrder] = useState('desc'); // Ordem: desc ou asc
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 9,
    total: 0,
    totalPages: 0
  });

  const loadCharacters = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(filters.search && {search: filters.search}),
        ...(filters.class && {class: filters.class}),
        ...(filters.minLevel && {minLevel: parseInt(filters.minLevel)}),
        ...(filters.maxLevel && {maxLevel: parseInt(filters.maxLevel)}),
        ...(filters.status && {status: filters.status}),
        ...(filters.userName && {userName: filters.userName}),
        sortBy: sortBy,
        sortOrder: sortOrder
      };

      const response = await charactersService.getAllCharacters(params);
      setCharacters(response.characters || []);
      setPagination(prev => ({
        ...prev,
        total: response.pagination?.total || 0,
        totalPages: response.pagination?.totalPages || 0
      }));
    } catch (err) {
      console.error('Error loading characters:', err);
      setError('Erro ao carregar personagens');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters, sortBy, sortOrder]);

  // Load characters on component mount and when filters change
  useEffect(() => {
    loadCharacters();
  }, [loadCharacters]);

  const handleCreateCharacter = async (characterData) => {
    try {
      setCreating(true);
      setError('');
      await charactersService.createCharacter(characterData);
      // Reload list to get updated data
      loadCharacters();
      setShowCreateModal(false);
    } catch (err) {
      console.error('Error creating character:', err);
      setError(err.response?.data?.error || 'Erro ao criar personagem');
    } finally {
      setCreating(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPagination(prev => ({...prev, page: 1})); // Reset to first page
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleFilterChange('search', searchInput);
    }
  };

  const handleUserNameKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleFilterChange('userName', userNameInput);
    }
  };

  const handleMinLevelKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleFilterChange('minLevel', minLevelInput);
    }
  };

  const handleMaxLevelKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleFilterChange('maxLevel', maxLevelInput);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({...prev, page: newPage}));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      class: '',
      minLevel: '',
      maxLevel: '',
      status: '',
      userName: ''
    });
    setSearchInput('');
    setUserNameInput('');
    setMinLevelInput('');
    setMaxLevelInput('');
    setSortBy('createdAt');
    setSortOrder('desc');
    setPagination(prev => ({...prev, page: 1}));
  };

  const handleEditCharacter = (character) => {
    setSelectedCharacter(character);
    setShowEditModal(true);
  };

  const handleUpdateCharacter = async (characterData) => {
    setEditing(true);
    setError('');

    try {
      await charactersService.updateCharacter(selectedCharacter.id, characterData);
      loadCharacters();
      setShowEditModal(false);
      setSelectedCharacter(null);
    } catch (err) {
      console.error('Error updating character:', err);
      setError(err.response?.data?.error || 'Erro ao atualizar personagem');
    } finally {
      setEditing(false);
    }
  };

  if (loading) {
    return (
      <PageContainer title="Personagens" description="Carregando...">
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg"/>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title={user?.role === 'ADMIN' ? 'Todos os Personagens' : 'Meus Personagens'}
      description={user?.role === 'ADMIN' ? 'Gerencie todos os chars do clã' : 'Gerencie seus chars'}
      actions={
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-2"/>
          Novo Personagem
        </Button>
      }
    >
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <motion.div
          initial={{opacity: 0, y: 20}}
          animate={{opacity: 1, y: 0}}
          transition={{duration: 0.3}}
        >
          <Card variant="glass"
                className="relative overflow-hidden group hover:scale-[1.02] transition-transform duration-300">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-transparent"></div>
            <div className="relative p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400 mb-1">Total</p>
                  <h3 className="text-3xl font-bold text-white">{pagination.total}</h3>
                </div>
                <div
                  className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center">
                  <Sword className="w-6 h-6 text-white"/>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>

        <motion.div
          initial={{opacity: 0, y: 20}}
          animate={{opacity: 1, y: 0}}
          transition={{duration: 0.3, delay: 0.1}}
        >
          <Card variant="glass"
                className="relative overflow-hidden group hover:scale-[1.02] transition-transform duration-300">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-transparent"></div>
            <div className="relative p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400 mb-1">Nível médio</p>
                  <h3 className="text-3xl font-bold text-white">
                    {parseInt((characters.reduce((acc, char) => acc + char.level, 0) / characters.length) || 0)}
                  </h3>
                </div>
                <div
                  className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-white"/>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>

        <motion.div
          initial={{opacity: 0, y: 20}}
          animate={{opacity: 1, y: 0}}
          transition={{duration: 0.3, delay: 0.2}}
        >
          <Card variant="glass"
                className="relative overflow-hidden group hover:scale-[1.02] transition-transform duration-300">
            <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-transparent"></div>
            <div className="relative p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400 mb-1">Ativos</p>
                  <h3 className="text-3xl font-bold text-white">
                    {characters.filter(char => char.status === 'ACTIVE').length}
                  </h3>
                </div>
                <div
                  className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                  <Shield className="w-6 h-6 text-white"/>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>

      {error && (
        <motion.div
          initial={{opacity: 0, y: -10}}
          animate={{opacity: 1, y: 0}}
          className="bg-red-500/10 border border-red-500/20 text-red-400 px-6 py-4 rounded-xl mb-6"
        >
          <div className="font-medium">{error}</div>
        </motion.div>
      )}

      {/* Filters */}
      <Card variant="glass" className="mb-8">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-6">
            <Filter className="w-5 h-5 text-cyan-400"/>
            <h3 className="text-lg font-semibold text-white">Filtros</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            <Input
              icon={Search}
              placeholder="Personagem... (Enter)"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              variant="glass"
            />

            <Input
              icon={Users}
              placeholder="Usuário... (Enter)"
              value={userNameInput}
              onChange={(e) => setUserNameInput(e.target.value)}
              onKeyPress={handleUserNameKeyPress}
              variant="glass"
            />

            <select
              value={filters.class}
              onChange={(e) => handleFilterChange('class', e.target.value)}
              className="bg-white/5 border border-white/10 text-white rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 transition-all"
            >
              <option value="" className="bg-slate-800">Todas as classes</option>
              <option value="ARQUEIRA" className="bg-slate-800">Arqueira</option>
              <option value="ASSASSINA" className="bg-slate-800">Assassina</option>
              <option value="ATALANTA" className="bg-slate-800">Atalanta</option>
              <option value="CAVALEIRO" className="bg-slate-800">Cavaleiro</option>
              <option value="GUERREIRA" className="bg-slate-800">Guerreira</option>
              <option value="LUTADOR" className="bg-slate-800">Lutador</option>
              <option value="MAGO" className="bg-slate-800">Mago</option>
              <option value="MECANICO" className="bg-slate-800">Mecânico</option>
              <option value="PIKE" className="bg-slate-800">Pike</option>
              <option value="SACERDOTISA" className="bg-slate-800">Sacerdotisa</option>
              <option value="XAMA" className="bg-slate-800">Xamã</option>
            </select>

            <Input
              type="number"
              placeholder="Nível mín. (Enter)"
              value={minLevelInput}
              onChange={(e) => setMinLevelInput(e.target.value)}
              onKeyPress={handleMinLevelKeyPress}
              variant="glass"
            />

            <Input
              type="number"
              placeholder="Nível máx. (Enter)"
              value={maxLevelInput}
              onChange={(e) => setMaxLevelInput(e.target.value)}
              onKeyPress={handleMaxLevelKeyPress}
              variant="glass"
            />

            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="bg-white/5 border border-white/10 text-white rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 transition-all"
            >
              <option value="" className="bg-slate-800">Todos os status</option>
              <option value="ACTIVE" className="bg-slate-800">Ativo</option>
              <option value="INACTIVE" className="bg-slate-800">Inativo</option>
            </select>
          </div>

          {/* Ordenação */}
          <div className="mt-6 pt-6 border-t border-white/10">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4 text-purple-400"/>
              <h4 className="text-sm font-semibold text-white">Ordenação</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-white/5 border border-white/10 text-white rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all"
              >
                <option value="createdAt" className="bg-slate-800">Data de Cadastro</option>
                <option value="name" className="bg-slate-800">Nome</option>
                <option value="level" className="bg-slate-800">Nível</option>
                <option value="totalPoints" className="bg-slate-800">Pontos Totais</option>
                <option value="class" className="bg-slate-800">Classe</option>
              </select>

              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="bg-white/5 border border-white/10 text-white rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all"
              >
                <option value="desc" className="bg-slate-800">
                  {sortBy === 'createdAt' ? 'Mais Recentes' : sortBy === 'name' || sortBy === 'class' ? 'Z → A' : 'Maior → Menor'}
                </option>
                <option value="asc" className="bg-slate-800">
                  {sortBy === 'createdAt' ? 'Mais Antigos' : sortBy === 'name' || sortBy === 'class' ? 'A → Z' : 'Menor → Maior'}
                </option>
              </select>
            </div>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:justify-between items-center">
            <div className="text-sm text-gray-400">
              {pagination.total} personagem(ns) encontrado(s)
              {(filters.search || filters.userName || filters.class || filters.minLevel || filters.maxLevel || (filters.status && filters.status !== 'ACTIVE')) && (
                <span className="ml-2 text-cyan-400">• Filtros ativos</span>
              )}
            </div>
            <Button variant="outline" onClick={clearFilters} size="sm">
              Limpar Filtros
            </Button>
          </div>
        </div>
      </Card>

      {characters.length === 0 ? (
        <EmptyState
          icon={Sword}
          title={user?.role === 'ADMIN' ? 'Nenhum herói encontrado' : 'Sua jornada começa aqui!'}
          description={
            user?.role === 'ADMIN'
              ? 'Ainda não há personagens criados no sistema.'
              : 'Crie seu primeiro herói e embarque em aventuras épicas!'
          }
          action={
            user?.role !== 'ADMIN' && (
              <Button onClick={() => setShowCreateModal(true)}>
                <Plus className="w-4 h-4 mr-2"/>
                Criar Meu Primeiro Herói
              </Button>
            )
          }
        />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {characters.map((character, index) => (
              <motion.div
                key={character.id}
                initial={{opacity: 0, y: 20}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.3, delay: index * 0.05}}
              >
                <Card variant="glass"
                      className="group hover:scale-[1.02] transition-all duration-300 cursor-pointer relative overflow-hidden">
                  {/* Gradient overlay on hover */}
                  <div
                    className="absolute inset-0 bg-gradient-to-br from-cyan-500/0 to-blue-600/0 group-hover:from-cyan-500/10 group-hover:to-blue-600/10 transition-all duration-300 pointer-events-none"></div>

                  <div className="relative p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center text-2xl">
                          {getClassTypeIcon(character.class)}
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-white">{character.name}</h3>
                          <p className="text-sm text-gray-400">{getClassTypeTranslation(character.class)}</p>
                          {character.user && (
                            <p className="text-xs text-cyan-400 mt-1 flex items-center gap-1">
                              <Users className="w-3 h-3"/>
                              {character.user.name}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                        character.status === 'ACTIVE'
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                          : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                      }`}>
                        {getCharacterStatsText(character.status)}
                      </div>
                    </div>

                    <div className="space-y-3 mb-4">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-400">Level</span>
                        <span className="font-semibold text-white">{character.level}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-400">Pontos Totais</span>
                        <span
                          className="font-semibold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-600">
                          {character.totalPoints}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-400 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Atualizado
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatRelativeTime(character.updatedAt)}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Button
                        size="sm"
                        fullWidth
                        onClick={() => navigate(`/app/characters/${character.id}`)}
                        variant="primary"
                      >
                        Ver Detalhes
                      </Button>

                      {(user?.role === 'ADMIN' || character.userId === user?.id) && (
                        <Button
                          size="sm"
                          variant="outline"
                          fullWidth
                          onClick={() => handleEditCharacter(character)}
                        >
                          Editar
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <motion.div
              initial={{opacity: 0}}
              animate={{opacity: 1}}
              transition={{duration: 0.3, delay: 0.3}}
              className="flex justify-center items-center mt-8 gap-2"
            >
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
              >
                Anterior
              </Button>

              <div className="flex gap-1">
                {Array.from({length: Math.min(5, pagination.totalPages)}, (_, i) => {
                  const pageNum = Math.max(1, Math.min(
                    pagination.totalPages - 4,
                    pagination.page - 2
                  )) + i;

                  if (pageNum > pagination.totalPages) return null;

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        pageNum === pagination.page
                          ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/50'
                          : 'bg-white/5 text-gray-400 hover:bg-white/10 border border-white/10'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
              >
                Próxima
              </Button>
            </motion.div>
          )}
        </>
      )}

      <CreateCharacterModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateCharacter}
        loading={creating}
      />

      <EditCharacterModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedCharacter(null);
        }}
        onSubmit={handleUpdateCharacter}
        character={selectedCharacter}
        loading={editing}
      />
    </PageContainer>
  );
};

export default Characters;